import React, { useEffect, useState, useContext } from "react";
import "./AddVenue.css";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "../../components/Loader";
import Pagination from "../../components/Pagination";
import { useTranslation } from 'react-i18next';
import AuthContext from "../../contexts/AuthProvider";
import { getUserRole, USER_ROLES } from "../../utils/UsersPermissions";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const Venues = ({ setIsEmpty, setFiles }) => {
  const { t, i18n } = useTranslation("addvenue");

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [venues, setVenues] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isDel, setIsDel] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleted, setDeleted] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
const { isAuthenticated } = useContext(AuthContext);

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const handleNavigateWithParams = (venue_id) => {
    navigate(`/venues/${venue_id}`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return;
      }
      if (!openDropdownId) return;

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/venues/${openDropdownId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error("Unauthorized access. Please log in again.");
        }
        const error = await response.json();
        throw new Error(error.error);
      }
      setDeleted((del) => ++del);
      setIsDel(false);
      setOpenDropdownId(null);
    } catch (error) {
      console.error("Error deleting the session:", error);
      alert(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchVenues = async (page = 1) => {
    try {
      if (!isAuthenticated) {
        setError(new Error("No authentication token found. Please log in."));
        setLoading(false);
        navigate("/");
        return { data: [], pagination: { currentPage: 1, totalPages: 1, totalItems: 0 } };
      }

      const venuesResponse = await fetch(`${baseUrl}/api/v1/attnd-venue/myVenues?page=${page}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (venuesResponse.status === 401) {
        navigate("/login");
        throw new Error("Unauthorized access. Please log in again.");
      }

      if (!venuesResponse.ok) {
        const venuesData = await venuesResponse.json();
        throw new Error(
          `An error occurred: ${venuesResponse.statusText || venuesData.error}`
        );
      }

      const venuesData = await venuesResponse.json();
      
      return {
        data: venuesData.data,
        pagination: {
          currentPage: venuesData.currentPage || page,
          totalPages: venuesData.totalPages || 1,
          totalItems: venuesData.totalItems || venuesData.data.length
        }
      };
    } catch (err) {
      setError(err);
      return { data: [], pagination: { currentPage: 1, totalPages: 1, totalItems: 0 } };
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      
      // Clear saved state
      localStorage.removeItem("appState");
      localStorage.removeItem("selectedAmenities");
      localStorage.removeItem("workingHours");
      localStorage.removeItem("pricingData");
      sessionStorage.removeItem("files");
      setFiles([]);

      const result = await fetchVenues(1);
      setVenues(result.data);
      setPagination(result.pagination);
      setIsEmpty(!result.data.length);
      setLoading(false);
    })();
  }, [baseUrl, deleted]);

  const handlePageChange = async (newPage) => {
    if (newPage === pagination.currentPage) return; // Prevent unnecessary requests
    
    setLoading(true);
    const result = await fetchVenues(newPage);
    setVenues(result.data);
    setPagination(result.pagination);
    setIsEmpty(!result.data.length);
    setLoading(false);
  };

  if (loading) {
    return (
      <div
        style={{
          height: "calc(100vh - 250px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="venue-venues-container">
        Error fetching venues: {error.message}
      </div>
    );
  }


  const canManageVenues = getUserRole() !== USER_ROLES.VIEWER;
  const rowClassName = canManageVenues
    ? "venueTable-row"
    : "venueTable-row venueTable-row--no-action";
  const headerClassName = canManageVenues
    ? "venueTable-header"
    : "venueTable-header venueTable-header--no-action";

  return (
    <div className={"EmptyVenue"}>
      {venues.length > 0 ? (
        <div className={"venueTable"}>
          <div className={headerClassName}>
            <p>{t("venues.table.header.name")}</p>
            <p>{t("venues.table.header.type")}</p>
            <p>{t("venues.table.header.capacity")}</p>
            <p>{t("venues.table.header.cost")}</p>
            {canManageVenues && <p>{t("venues.table.header.action")}</p>}
          </div>
          {venues.map((venue) => {
            const venueTypeLabel =
              i18n.language === "ar" && venue.venue_type?.title_ar
                ? venue.venue_type.title_ar
                : venue.venue_type?.title;
            const costLabel =
              venue.hourly_rate && Number(venue.hourly_rate) !== 0
                ? `${Number(venue.hourly_rate).toLocaleString()} ${t("cost.hourly")}`
                : `${Number(venue.daily_rate).toLocaleString()} ${t("cost.daily")}`;
            const perPerson =
              venue.per_attendees === true ||
              venue.per_attendees === 1 ||
              venue.per_attendees === "1" ||
              venue.per_attendees === "true";

            return (
              <div key={venue.id} style={{ position: "relative", width: "100%" }}>
                <div
                  className={rowClassName}
                  onClick={() => {
                    localStorage.setItem("venueId", venue.id);
                    handleNavigateWithParams(venue.id);
                  }}
                >
                  <p className="venueTable-cell" title={venue.name}>
                    {venue.name}
                  </p>
                  <p className="venueTable-cell" title={venueTypeLabel}>
                    {venueTypeLabel}
                  </p>
                  <p className="venueTable-cell">{venue.capacity}</p>
                  <p className="venueTable-cell venueTable-cell--cost" title={costLabel}>
                    {costLabel}
                    {perPerson && <span> / {t("cost.person")}</span>}
                  </p>
                  {canManageVenues && (
                    <p
                      className="venueTable-cell venueTable-cell--action"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDropdownId === venue.id && setIsDel(false);
                        setOpenDropdownId(
                          openDropdownId === venue.id ? null : venue.id,
                        );
                      }}
                    >
                      <svg
                        style={{ cursor: "pointer", width: "24px", height: "24px" }}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z"
                          fill="#5507CF"
                        />
                        <path
                          d="M12 13C11.44 13 11 12.55 11 12C11 11.45 11.45 11 12 11C12.55 11 13 11.45 13 12C13 12.55 12.56 13 12 13Z"
                          fill="#5507CF"
                        />
                        <path
                          d="M16 13C15.44 13 15 12.55 15 12C15 11.45 15.45 11 16 11C16.55 11 17 11.45 17 12C17 12.55 16.56 13 16 13Z"
                          fill="#5507CF"
                        />
                        <path
                          d="M8 13C7.44 13 7 12.55 7 12C7 11.45 7.45 11 8 11C8.55 11 9 11.45 9 12C9 12.55 8.56 13 8 13Z"
                          fill="#5507CF"
                        />
                      </svg>
                    </p>
                  )}
                </div>
                {openDropdownId === venue.id && canManageVenues && (
                  <div className={"DropDown"}>
                    <div
                      onClick={() => {
                        localStorage.setItem("venueId", venue.id);
                        navigate({
                          pathname: location.pathname,
                          search: "?step=1&edit=true",
                        });
                      }}
                    >
                      {t("venues.dropdown.edit")}
                    </div>
                    <div style={{ color: "red" }} onClick={() => setIsDel(true)}>
                      {t("venues.dropdown.delete")}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <img src="/empty state14 1.svg" alt="" />
          <h4>{t("venues.emptyState.title")}</h4>
          <p>{t("venues.emptyState.description")}</p>
          {/* Hide add venue button for viewers */}
          {getUserRole() !== USER_ROLES.VIEWER && (
            <button onClick={() => navigate("/venues?step=1")}>
              <img src="/add.svg" alt="" />
              {t("venues.emptyState.button")}
            </button>
          )}
        </>
      )}

      {/* Pagination Component */}
      {venues.length > 0 && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          handlePageChange={handlePageChange}
          maxVisiblePages={5}
          isRTL={getLangFromLocalStorage() === "ar"}
        />
      )}

      {openDropdownId && isDel && (
        <div className="delete-event-modal-overlay">
          <div className="delete-account-modal">
            <div className={"Header"}>
              {t("venues.deleteModal.title")}
              <svg
                onClick={() => setIsDel(false)}
                style={{ cursor: "pointer" }}
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="30" height="30" rx="15" fill="#DBDBDB" />
                <path
                  d="M9.92188 20.4531C9.79167 20.3229 9.70573 20.1719 9.66406 20C9.6224 19.8229 9.6224 19.6484 9.66406 19.4766C9.70573 19.3047 9.78906 19.1562 9.91406 19.0312L13.5781 15.3672L9.91406 11.7109C9.78906 11.5859 9.70573 11.4375 9.66406 11.2656C9.6224 11.0938 9.6224 10.9219 9.66406 10.75C9.70573 10.5729 9.79167 10.4193 9.92188 10.2891C10.0469 10.1641 10.1953 10.0807 10.3672 10.0391C10.5443 9.99219 10.7188 9.99219 10.8906 10.0391C11.0677 10.0807 11.2188 10.1615 11.3438 10.2812L15 13.9453L18.6641 10.2891C18.7891 10.1641 18.9349 10.0807 19.1016 10.0391C19.2734 9.99219 19.4453 9.99219 19.6172 10.0391C19.7943 10.0807 19.9453 10.1667 20.0703 10.2969C20.2057 10.4219 20.2943 10.5729 20.3359 10.75C20.3776 10.9219 20.3776 11.0938 20.3359 11.2656C20.2943 11.4375 20.2083 11.5859 20.0781 11.7109L16.4297 15.3672L20.0781 19.0312C20.2083 19.1562 20.2943 19.3047 20.3359 19.4766C20.3776 19.6484 20.3776 19.8229 20.3359 20C20.2943 20.1719 20.2057 20.3203 20.0703 20.4453C19.9453 20.5755 19.7943 20.6641 19.6172 20.7109C19.4453 20.7526 19.2734 20.7526 19.1016 20.7109C18.9349 20.6693 18.7891 20.5833 18.6641 20.4531L15 16.7969L11.3438 20.4609C11.2188 20.5807 11.0677 20.6641 10.8906 20.7109C10.7188 20.7526 10.5443 20.7526 10.3672 20.7109C10.1953 20.6641 10.0469 20.5781 9.92188 20.4531Z"
                  fill="#464646"
                />
              </svg>
            </div>
            <div className={"Content"}>
              <div className={"Info"}>
                <h5>{t("venues.deleteModal.prompt")}</h5>
                <p style={{ width: "100%", textAlign: "left" }}>
                  {t("venues.deleteModal.warning")}
                </p>
              </div>
              <div className={"Buttons"}>
                <button onClick={() => setIsDel(false)} className={"Cancel"}>
                  {t("venues.deleteModal.cancel")}
                </button>
                <button className={"Del"} onClick={handleDelete}>
                  {isDeleting ? t("venues.deleteModal.confirming") : t("venues.deleteModal.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Venues;
